import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NoShowForm from './NoShowForm';
import NoShowChart, { processChartData } from './NoShowChart';
import NoShowAnalytics from './NoShowAnalytics';
import NoShowPDF from './NoShowPDF';
import { analyticsDataService } from '../../../services/analyticsDataService';

const CHART_TYPE = 'no-show-analysis';

const NoShowAnalysis = ({ user, viewData = null, readOnly = false }) => {
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const pdfChartRef = useRef(null);
  const [view, setView] = useState(viewData ? 'chart' : 'select');
  const [savedData, setSavedData] = useState(
    viewData ? { data: viewData, timestamp: new Date().toISOString(), recordCount: viewData.length } : null
  );
  const [hasExistingData, setHasExistingData] = useState(!!(viewData && viewData.length));

  useEffect(() => {
    if (viewData) return; // read-only supervisor view — data is injected via props
    if (user?.id) {
      analyticsDataService.setCurrentUser(user.id);
    }
    checkExistingData();

    // Cloud sync: pull newer data from cloud
    if (user?.id) {
      analyticsDataService.syncFromCloud(CHART_TYPE).then(cloudData => {
        if (cloudData) {
          setSavedData(cloudData);
          setHasExistingData(true);
        }
      });
    }
  }, [user, viewData]);

  const checkExistingData = () => {
    const existing = analyticsDataService.hasChartData(CHART_TYPE);
    setHasExistingData(existing);
    if (existing) {
      const data = analyticsDataService.getChartData(CHART_TYPE);
      setSavedData(data);
    }
  };

  const handleFormSave = (data) => {
    const result = analyticsDataService.saveChartData(CHART_TYPE, data);
    if (result.success) {
      setSavedData({ data, timestamp: result.timestamp, recordCount: result.recordCount });
      setHasExistingData(true);
      setView('chart');
    }
  };

  const handleFormCancel = () => {
    setView('select');
  };

  const chartData = savedData?.data ? processChartData(savedData.data) : null;

  return (
    <div className="h-full overflow-y-auto p-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        {!readOnly && (
        <button
          onClick={() => navigate('/analytics')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <span className="mr-2">←</span>
          Back to Charts
        </button>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">No-Show Analysis</h1>
          <p className="text-gray-600 mt-2">Identify attendance patterns and optimize client engagement across your caseload</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-6">
          {!readOnly && (
          <button
            onClick={() => setView('form')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              view === 'form'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Enter New Data
          </button>
          )}
          <button
            onClick={() => setView('chart')}
            disabled={!hasExistingData}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              view === 'chart'
                ? 'bg-blue-600 text-white shadow-md'
                : !hasExistingData
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Visualize Data
            {hasExistingData && savedData && (
              <span className="ml-2 text-xs opacity-75">({savedData.recordCount} clients)</span>
            )}
          </button>
          <button
            onClick={() => setView('analytics')}
            disabled={!hasExistingData}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              view === 'analytics'
                ? 'bg-blue-600 text-white shadow-md'
                : !hasExistingData
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            View Analytics
          </button>
          <NoShowPDF
            chartData={chartData}
            chartRef={pdfChartRef}
            user={user}
          />
        </div>

        {/* Content Area */}
        {view === 'select' && (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <p className="text-gray-500">Select an option above to get started</p>
            <p className="text-sm text-gray-400 mt-2">
              Enter client IDs, total appointments, and no-shows to analyze attendance patterns.
            </p>
            {hasExistingData && savedData && (
              <p className="text-sm text-gray-400 mt-2">
                Last updated: {new Date(savedData.timestamp).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {view === 'form' && (
          <NoShowForm
            onSave={handleFormSave}
            onCancel={handleFormCancel}
            existingData={savedData?.data || []}
          />
        )}

        {view === 'chart' && chartData && (
          <div>
            <div ref={chartRef}>
              <NoShowChart chartData={chartData} />
            </div>
            <p className="text-sm text-gray-400 mt-4 text-center">
              {chartData.totalClients} clients | Last updated: {new Date(savedData.timestamp).toLocaleString()}
            </p>
          </div>
        )}

        {view === 'analytics' && chartData && (
          <div>
            <NoShowAnalytics chartData={chartData} />
            <p className="text-sm text-gray-400 mt-4 text-center">
              {chartData.totalClients} clients | Last updated: {new Date(savedData.timestamp).toLocaleString()}
            </p>
          </div>
        )}

        {/* Hidden chart for PDF capture */}
        {chartData && (
          <div
            ref={pdfChartRef}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '1200px',
              minHeight: '600px'
            }}
            aria-hidden="true"
          >
            <NoShowChart chartData={chartData} pdfMode={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default NoShowAnalysis;
